import { useState, useEffect, useRef } from "react";
import './styles/modal.css'
import AnimatedSlide from "./AnimatedSlide";
import bebasFont from '../../../public/bebas-neue-v9-latin-regular.woff';
import clickSound from '../../Interface/click_Sound.mp3';
import '../../styles/style.css';
import Slider from "./slider";

const clickSoundInstance = new Audio(clickSound);
clickSoundInstance.volume = 0.5;

const Modal2D = ({ isOpen, closeModal, slides, setSlides, sliderIndex, setNewSliderIndex }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(sliderIndex);
    const [isAnimating, setIsAnimating] = useState(false);
    const [animationDirection, setAnimationDirection] = useState('');
    const animationTimeoutRef = useRef(null); 

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                handleClose();
            }
        };

        if (isOpen) {
            setShouldRender(true);
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
            
            // Trigger fade in after render
            const fadeInTimer = setTimeout(() => {
                setIsVisible(true);
            }, 50);

            return () => {
                clearTimeout(fadeInTimer);
                document.removeEventListener('keydown', handleEscape);
            };
        } else if (shouldRender) {
            // Start fade out immediately
            setIsVisible(false);
            
            // Keep rendered during fade out transition
            const fadeOutTimer = setTimeout(() => {
                setShouldRender(false);
                document.body.style.overflow = 'unset';
            }, 300); 

            return () => {
                clearTimeout(fadeOutTimer);
                document.removeEventListener('keydown', handleEscape);
            };
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, shouldRender]);

    // Handle slide animations when index changes
    useEffect(() => {
        // Clear any existing animation timeout
        if (animationTimeoutRef.current) {
            clearTimeout(animationTimeoutRef.current);
            animationTimeoutRef.current = null;
        }

        // Only animate if index actually changed
        if (currentSlideIndex !== sliderIndex) {
            const direction = sliderIndex > currentSlideIndex ? 'right' : 'left';
            setAnimationDirection(direction);
            setIsAnimating(true);
            
            // Update the actual displayed slide after animation completes
            animationTimeoutRef.current = setTimeout(() => {
                setCurrentSlideIndex(sliderIndex);
                setIsAnimating(false);
                setAnimationDirection('');
                animationTimeoutRef.current = null;
            }, 500);
        }

        // Cleanup function
        return () => {
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
                animationTimeoutRef.current = null;
            }
        };
    }, [sliderIndex]); 

    // Billboard click sound
    const playClickSound = () => {
        clickSoundInstance.currentTime = 0; 
        clickSoundInstance.play();
    }

    const handleClose = () => {
        playClickSound();
        closeModal();
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    if (!shouldRender) return null;

    return (
        <div 
            className={`modal_overlay ${isVisible ? 'modal_fade_in' : 'modal_fade_out'}`} 
            onClick={handleOverlayClick}
        >
            <div 
                className={`modal_content ${isVisible ? 'modal_content_fade_in' : 'modal_content_fade_out'}`} 
                onClick={(e) => e.stopPropagation()}
                style={{
                    fontFamily: bebasFont
                }}
            >
                <div className="modal_header">
                    <div className="modal_exit_container">
                        <button className="exitButton" onClick={handleClose}></button>
                    </div>
                </div>
                
                <Slider sliderIndex={sliderIndex} setNewSliderIndex={setNewSliderIndex}/>

                <div className="modal_body">
                    {/* Static current slide (no animation) */}
                    {!isAnimating && (
                        <div className="slide_wrapper">
                            {slides[currentSlideIndex]}
                        </div>
                    )}

                    {/* Exit animation */}
                    {isAnimating && (
                        <AnimatedSlide
                            animationType="exit"
                            direction={animationDirection === 'right' ? 'left' : 'right'}
                            index={currentSlideIndex}
                        >
                            {slides[currentSlideIndex]}
                        </AnimatedSlide>
                    )}

                    {/* Enter animation */}
                    {isAnimating && (
                        <AnimatedSlide
                            animationType="enter"
                            direction={animationDirection}
                            index={sliderIndex}
                        >
                            {slides[sliderIndex]}
                        </AnimatedSlide>
                    )}
                </div>

                {/* Slider */}
                <Slider sliderIndex={sliderIndex} setNewSliderIndex={setNewSliderIndex}/>
            </div>
        </div>
    );
};

export default Modal2D;